'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

import { TbSquareRoundedChevronDown } from 'react-icons/tb';
import { GrBasket } from 'react-icons/gr';
import { IoMdStar } from 'react-icons/io';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Paragraph } from '../ui/paragraph';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export function Hero({
  subtitle,
  description,
  image,
}: {
  subtitle: string;
  description: string;
  image: string;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const sectionRef = useRef<HTMLElement | null>(null);
  const handleScrollDown = () => {
    const section = sectionRef.current;
    if (!section) return;
    window.scrollTo({
      top: section.getBoundingClientRect().bottom + window.scrollY,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  const localeId = locale === 'en' ? 'our-services' : 'nase-sluzby';

  return (
    <section
      ref={sectionRef}
      className={cn(
        'custom-section',
        'relative overflow-hidden rounded-b-[40px] pb-[124px] pt-32 sm:rounded-b-[60px] sm:pt-64 md:pt-[260px] lg:rounded-b-[80px] xl:pt-[355px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-20',
        )}
      >
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4">
          <p data-ea-visual-tag="h3" className="font-normal leading-[1.4] text-white">
            {subtitle}
          </p>
          <h1 className="max-w-[800px] text-balance text-[40px] font-semibold leading-[1.2] tracking-tight text-white sm:text-5xl sm:leading-[1.2] md:text-6xl md:leading-[1.2] lg:text-[64px] lg:leading-[1.2]">
            <span className="relative">
              {t('text3349')}
              <Image
                src="/images/hero-line.svg"
                alt=""
                height={0}
                width={0}
                className="absolute -bottom-1.5 left-0 z-[-1] h-auto w-full object-contain object-center"
              />
            </span>
            &nbsp; {t('text3350')}
          </h1>
          <Paragraph innerHtml={description} className="w-full max-w-[590px]" color="white" />
          <div className="flex items-center justify-start gap-4">
            <Button asChild variant={'white'}>
              <Link href={'/eshop'}>
                <GrBasket size={20} /> {t('text172')}
              </Link>
            </Button>
            <Button asChild variant={'light-white'}>
              <Link href={`#${localeId}`}>{t('text1345')}</Link>
            </Button>
          </div>
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-x-4 gap-y-16 sm:flex-row sm:items-end sm:justify-between">
          <Button
            onClick={handleScrollDown}
            variant={'ghost-white'}
            size={'none'}
            className="whitespace-nowrap"
          >
            <TbSquareRoundedChevronDown size={28} /> {t('text2313')}
          </Button>
          <ReviewCard />
        </div>
      </div>

      <div className="absolute inset-0 z-[1] h-full w-full bg-black/50" />
      <Image
        src={image}
        alt="AZEKO"
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        priority
        loading="eager"
        sizes="100vw"
      />
    </section>
  );
}

function ReviewCard() {
  const { t } = useTranslations();

  const avatars = ['/images/avatars/1.jpg', '/images/avatars/2.jpg', '/images/avatars/3.jpg'];

  return (
    <div className="flex w-full items-center justify-start gap-4 rounded-[24px] bg-white/20 p-6 backdrop-blur-[8px] sm:max-w-[374px] sm:p-8">
      <div className="flex items-center justify-start gap-0">
        {avatars.map((avatar, index) => (
          <Image
            key={index}
            src={avatar}
            alt={`Avatar ${index + 1}`}
            width={56}
            height={56}
            className={cn(
              'max-h-14 min-h-14 min-w-14 max-w-14 overflow-hidden rounded-full border-2 border-white object-cover object-center',
              index !== 0 && '-ml-6',
            )}
            sizes="56px"
          />
        ))}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-1">
        <div className="flex items-center justify-start gap-2">
          <p className="text-white">{t('text3317')}</p>
          <p className="text-white">4.9/5</p>
          <IoMdStar size={20} className="text-[#EDB000]" />
        </div>
        <p className="whitespace-nowrap font-bold text-white">{t('text3318')}</p>
      </div>
    </div>
  );
}
